import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { Box, Button, CircularProgress, Divider, TextField, Typography, useTheme } from "@mui/material";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Fetch from "../../services/Fetch";
import { useConstants } from "../../hooks/UseConstants";
import { useWaits } from "../../hooks/UseWait";
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";
import { FormattedMessage } from "react-intl";
import AdminPageHero from "../../components/AdminPageHero";

const CONTACT_FIELDS = [
    'contact_us_email', 'contact_us_phone', 'contact_us_whatsapp_number',
    'contact_us_website', 'contact_us_facebook', 'contact_us_instagram',
];

const LANG_FIELDS = ['terms_of_use', 'privacy_policy', 'about_us'];

function Settings() {
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { host, language } = useConstants();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [config, setConfig] = useState({});

    const loadConfig = async () => {
        const result = await Fetch(host + '/admin/config', 'GET', null);
        if (result.status === 200) setConfig(result.data.data || {});
        setGetWait(false);
    };

    const updateField = (key, value) => {
        setConfig((prev) => ({ ...prev, [key]: value }));
    };

    const save = async () => {
        setSendWait(true);
        const formData = new FormData();
        Object.entries(config).forEach(([key, value]) => {
            if (value !== undefined && value !== null) formData.append(key, value);
        });
        const result = await Fetch(host + '/admin/config/update', 'POST', formData);
        if (result.status === 200) {
            setSnackBar('success', <FormattedMessage id="updated_success" />);
        } else if (result.status === 422) {
            setSnackBar('error', result.data.errors?.[0] || result.data.message);
        }
        setSendWait(false);
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            loadConfig();
        }
    }, [wait]);

    const langSuffix = language === 'en' ? '_en' : '_ar';

    return (
        <>
            {wait || getWait ? (
                <Box className="w-full h-screen flex justify-center items-center"><CircularProgress size={70} /></Box>
            ) : (
                <Box sx={{ backgroundColor: theme.palette.background.paper, float: language === 'en' ? 'right' : undefined }} className="w-4/5 rounded-xl px-6 py-6 shadow-lg main-layout-content" dir={language === 'en' ? 'ltr' : 'rtl'}>
                    <AdminPageHero />
                    <Divider className="!my-4" />

                    <Typography variant="h6" className="!mb-3"><FormattedMessage id="revenue_distribution_settings" /></Typography>
                    <TextField
                        fullWidth
                        type="number"
                        label={<FormattedMessage id="platform_commission_percentage" />}
                        value={config.admin_percentage ?? ''}
                        onChange={(e) => updateField('admin_percentage', e.target.value)}
                        inputProps={{ min: 0, max: 100 }}
                    />

                    <Typography variant="h6" className="!mt-6 !mb-3"><FormattedMessage id="contact_settings" /></Typography>
                    <Box className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                        {CONTACT_FIELDS.map((key) => (
                            <TextField
                                key={key}
                                fullWidth
                                label={key.replace(/_/g, ' ')}
                                value={config[key] ?? ''}
                                onChange={(e) => updateField(key, e.target.value)}
                            />
                        ))}
                    </Box>

                    <Typography variant="h6" className="!mt-6 !mb-3"><FormattedMessage id="content_settings" /></Typography>
                    {LANG_FIELDS.map((base) => {
                        const key = `${base}${langSuffix}`;
                        return (
                            <TextField
                                key={key}
                                fullWidth
                                multiline
                                minRows={3}
                                className="!mb-4"
                                label={`${base} (${language})`}
                                value={config[key] ?? ''}
                                onChange={(e) => updateField(key, e.target.value)}
                            />
                        );
                    })}

                    <Button variant="contained" className="!mt-4" onClick={save} disabled={sendWait}>
                        {sendWait ? <CircularProgress size={22} color="inherit" /> : <><SaveOutlinedIcon /><FormattedMessage id="save_changes" /></>}
                    </Button>

                    <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
                </Box>
            )}
        </>
    );
}

export default Settings;
